import { useEffect, useLayoutEffect, useRef, useState } from "react";

// Mantine's default modal z-index (`--mantine-z-index-modal`). Stacked modals
// sit at BASE + open-order, staying below popover/dropdown level (300) so
// dropdowns inside a modal still layer above it.
const BASE_MODAL_Z_INDEX = 200;

// Module-level stack shared by every modal that opts in. `openCount` tracks how
// many are currently open; `sequence` only ever climbs while something is open
// and resets once the stack empties, so values stay small (never near
// Mantine's 9999 ceiling).
let openCount = 0;
let sequence = 0;

function release() {
  openCount -= 1;
  if (openCount <= 0) {
    openCount = 0;
    sequence = 0;
  }
}

/**
 * Returns a z-index that guarantees the most recently opened modal renders on
 * top of any already-open modal, regardless of DOM order — the same rule
 * Mantine's `Modal.Stack` applies, but usable across modals that live in
 * decoupled components (a full-screen chart, the drill-down it opens, an image
 * grid, the image detail inside it, …) and so can't share one `Modal.Stack`.
 *
 * Returns `undefined` while closed, so Mantine's default z-index applies.
 */
export function useStackedModalZIndex(opened: boolean): number | undefined {
  const [zIndex, setZIndex] = useState<number>();
  // Whether this modal currently holds a slot in the shared stack. Guards
  // against double-acquire / double-release (incl. React StrictMode's
  // mount→cleanup→mount effect double-invocation in dev).
  const isRegistered = useRef(false);

  // Layout effect so the stacked z-index is committed before the browser
  // paints — otherwise a zero-transition dialog can flash one frame at the
  // default z-index (behind an already-open modal) before correcting.
  useLayoutEffect(() => {
    if (opened && !isRegistered.current) {
      isRegistered.current = true;
      openCount += 1;
      sequence += 1;
      setZIndex(BASE_MODAL_Z_INDEX + sequence);
    } else if (!opened && isRegistered.current) {
      isRegistered.current = false;
      release();
      setZIndex(undefined);
    }
  }, [opened]);

  // Release the slot if the modal unmounts while still open (e.g. a full-screen
  // dialog that is conditionally rendered and simply removed on close).
  useEffect(
    () => () => {
      if (isRegistered.current) {
        isRegistered.current = false;
        release();
      }
    },
    [],
  );

  return zIndex;
}
